import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, PhoneForwarded, Settings2, Sparkles, Voicemail, WandSparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface CallsEmptyStateProps {
  onLoadSampleCalls: () => void;
  onOpenCallerExperience: () => void;
}
interface GuidanceStep {
  icon: typeof PhoneForwarded;
  title: string;
  detail: string;
}
const GUIDANCE_STEPS: GuidanceStep[] = [{
  icon: PhoneForwarded,
  title: 'Calls arrive on your number',
  detail: 'Forwarding is active — Vexu picks up when you can’t.'
}, {
  icon: Voicemail,
  title: 'Vexu answers and takes notes',
  detail: 'You get the full transcript plus a short summary.'
}, {
  icon: CalendarDays,
  title: 'Bookings land in your calendar',
  detail: 'Meetings your AI schedules show up on the call.'
}];
export function CallsEmptyState({
  onLoadSampleCalls,
  onOpenCallerExperience
}: CallsEmptyStateProps) {
  const {
    isLight
  } = useTheme();
  return <div className="px-5 pt-4 pb-8" data-id="element-156" data-mp="2gk3if8va1hdc">
      <motion.section data-tour="calls-list-item" initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} className={`relative overflow-hidden rounded-3xl border px-5 pt-8 pb-6 text-center ${isLight ? 'border-slate-200/80 bg-white shadow-[0_2px_14px_rgba(15,23,42,0.05)]' : 'border-white/[0.07] bg-[#111111]'}`} data-id="element-157" data-mp="2blitw6pe0m4q">
        <div aria-hidden="true" className={`pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full blur-3xl ${isLight ? 'bg-vexu/10' : 'bg-vexu/[0.14]'}`} data-id="element-158" data-mp="1suiyamne0e2h" />

        <div className="relative mx-auto mb-5 grid h-16 w-16 place-items-center" data-id="element-159" data-mp="1jvdzrgbtskqb">
          <motion.span aria-hidden="true" animate={{
          scale: [1, 1.18, 1],
          opacity: [0.45, 0, 0.45]
        }} transition={{
          duration: 2.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border border-vexu/40" data-id="element-160" data-mp="1evt4bfvv6uic" />
          <span className={`relative grid h-14 w-14 place-items-center rounded-2xl ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/[0.12] text-vexu'}`} data-id="element-161" data-mp="1586zq5nvp767">
            <Voicemail className="h-6 w-6" data-id="element-162" data-mp="v9hsd6tlz547" />
          </span>
        </div>

        <h2 className={`text-lg font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-163" data-mp="vbkehfrn4e3n">
          No calls yet
        </h2>
        <p className={`mx-auto mt-2 max-w-[16rem] text-[12.5px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-164" data-mp="1nx3su67jhtmu">
          Vexu is standing by. Every answered call appears here with its
          transcript, summary, and any meetings booked.
        </p>

        <ul className="mt-6 space-y-2.5 text-left" data-id="element-165" data-mp="21xob919jlnft">
          {GUIDANCE_STEPS.map((step, index) => <motion.li key={step.title} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12 + index * 0.07,
          duration: 0.3
        }} className={`flex items-start gap-3 rounded-2xl border px-3 py-2.5 ${isLight ? 'border-slate-200/70 bg-slate-50' : 'border-white/[0.06] bg-white/[0.03]'}`} data-id="element-166" data-mp="26owq4ctn610d" data-mpi={index}>
              <span className={`mt-0.5 grid h-7 w-7 flex-shrink-0 place-items-center rounded-xl ${isLight ? 'bg-white text-vexu-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)]' : 'bg-vexu/[0.12] text-vexu'}`} data-id="element-167" data-mp="1fhl1lpo6d5">
                <step.icon className="h-3.5 w-3.5" data-id="element-168" data-mp="33ixqzdh4tlfo" />
              </span>
              <span className="min-w-0" data-id="element-169" data-mp="3hhnmiooazd1x">
                <span className={`block text-[12.5px] font-semibold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-170" data-mp="3hejbp8gc1j2a">
                  {step.title}
                </span>
                <span className={`mt-0.5 block text-[11px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-171" data-mp="spblaq9p6yjf">
                  {step.detail}
                </span>
              </span>
            </motion.li>)}
        </ul>

        <button type="button" onClick={onLoadSampleCalls} className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-vexu px-4 py-3 text-sm font-bold text-white shadow-[0_6px_18px_rgba(0,200,83,0.22)] transition-all hover:bg-vexu-600 active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2" data-id="element-172" data-mp="1wzwjng9ixjih">
          <WandSparkles className="h-4 w-4" data-id="element-173" data-mp="2fqo2r9jbcoa2" />
          Simulate call history
        </button>
        <p className={`mt-2 text-[10.5px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-174" data-mp="2kp7p3b1tk22y">
          Loads example calls so you can explore the experience.
        </p>
      </motion.section>

      <button type="button" onClick={onOpenCallerExperience} className={`mt-3 flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${isLight ? 'border-slate-200/80 bg-white hover:bg-slate-50' : 'border-white/[0.06] bg-[#111111] hover:bg-white/[0.04]'}`} data-id="element-175" data-mp="2un5cjwbutuui">
        <span className="flex min-w-0 items-center gap-3" data-id="element-176" data-mp="92nn7cn9tpnl">
          <span className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-xl ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/[0.06] text-zinc-300'}`} data-id="element-177" data-mp="43kgzovk1de1">
            <Settings2 className="h-4 w-4" data-id="element-178" data-mp="wrzwecillxpw" />
          </span>
          <span className="min-w-0" data-id="element-179" data-mp="wshxpnk4b24o">
            <span className={`block text-[12.5px] font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-180" data-mp="s0q8bgcp72g4">
              Set up your caller experience
            </span>
            <span className={`mt-0.5 block text-[11px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-181" data-mp="1kh0zrlckqiob">
              Choose the greeting, tone, and booking rules first.
            </span>
          </span>
        </span>
        <Sparkles className={`h-4 w-4 flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-182" data-mp="39yy181sr8f5o" />
      </button>
    </div>;
}